/**
 * Deterministic sample dataset for DEMO MODE and for generating supabase/seed.sql.
 * Dates are relative to "today" so Active / Expiring / Expired states are always meaningful.
 * All organizations and people here are fictional.
 */
import type {
  Amendment,
  AppNotification,
  AppUser,
  AuditLog,
  Department,
  FinancialTransaction,
  Location,
  QuantityTransaction,
  Tender,
  TenderDocument,
  TenderLifecycleStatus,
  TenderType,
  TenderingMethod,
  TenderCategory,
  FloatedBy,
  Vendor,
  DocumentType,
  TenderLineItem,
} from '../../types/domain';
import { addDays, todayISO } from '../../lib/dates';

// ---- deterministic PRNG (mulberry32) so every run produces the same data
function rng(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable pseudo-UUID from a namespace + index (valid v4 layout). */
function uid(ns: number, i: number): string {
  const h = (n: number, len: number) => n.toString(16).padStart(len, '0').slice(-len);
  return `${h(ns, 8)}-${h(i, 4)}-4${h(ns * 7 + i, 3)}-a${h(i * 13 + ns, 3)}-${h(ns * 1_000_003 + i * 97, 12)}`;
}

export interface SeedData {
  users: AppUser[];
  departments: Department[];
  locations: Location[];
  vendors: Vendor[];
  tenders: Tender[];
  quantity: QuantityTransaction[];
  financials: FinancialTransaction[];
  documents: TenderDocument[];
  amendments: Amendment[];
  audit: AuditLog[];
  notifications: AppNotification[];
  /** Line items (none in the sample data; added in the app). */
  lineItems?: TenderLineItem[];
}

/** The 10 departments (same codes as migration 20261022120000_departments_order_type_budget.sql). */
const DEPARTMENTS: [string, string][] = [
  ['SALES', 'Sales'],
  ['FIN', 'Finance'],
  ['LOG', 'Logistics'],
  ['HSSE', 'HSSE'],
  ['GOV', 'Governance Cell'],
  ['CRR', 'Complaints & Redressal'],
  ['HR', 'HR'],
  ['CRET', 'Consumer Retailing'],
  ['OPS', 'Operations'],
  ['HSSE-AUD', 'HSSE Audit'],
];

const LOCATIONS: [string, string, string][] = [
  ['DEL', 'New Delhi HQ', 'Delhi'],
  ['MUM', 'Mumbai Regional Office', 'Maharashtra'],
  ['KOL', 'Kolkata Regional Office', 'West Bengal'],
  ['CHE', 'Chennai Regional Office', 'Tamil Nadu'],
  ['BLR', 'Bengaluru Tech Centre', 'Karnataka'],
  ['HYD', 'Hyderabad Plant', 'Telangana'],
  ['AHM', 'Ahmedabad Depot', 'Gujarat'],
  ['LKO', 'Lucknow Zonal Office', 'Uttar Pradesh'],
  ['BBS', 'Bhubaneswar Unit', 'Odisha'],
  ['GUW', 'Guwahati Unit', 'Assam'],
];

const VENDORS: [string, string, string, boolean][] = [
  ['Arcadia Infra Systems Pvt Ltd', 'Mumbai', 'Maharashtra', false],
  ['Bharat Techno Supplies', 'New Delhi', 'Delhi', true],
  ['Coastline Facility Services LLP', 'Chennai', 'Tamil Nadu', true],
  ['Deccan Electricals Ltd', 'Hyderabad', 'Telangana', false],
  ['Eastern Office Solutions', 'Kolkata', 'West Bengal', true],
  ['Fortline Security Services', 'Lucknow', 'Uttar Pradesh', true],
  ['Granite Peak Engineering Co', 'Ahmedabad', 'Gujarat', false],
  ['Horizon Digital Networks Pvt Ltd', 'Bengaluru', 'Karnataka', false],
  ['Indus Medical & Safety Gear', 'New Delhi', 'Delhi', true],
  ['Jaipur Fleet & Logistics', 'Jaipur', 'Rajasthan', true],
  ['Kaveri Printing & Stationery', 'Bengaluru', 'Karnataka', true],
  ['Lotus Cleanserve Enterprises', 'Bhubaneswar', 'Odisha', true],
  ['Meridian Power Equipment Ltd', 'Pune', 'Maharashtra', false],
  ['Northstar Software Services', 'Noida', 'Uttar Pradesh', false],
];

const STATE_CODES: Record<string, string> = {
  Maharashtra: '27', Delhi: '07', 'Tamil Nadu': '33', Telangana: '36', 'West Bengal': '19',
  'Uttar Pradesh': '09', Gujarat: '24', Karnataka: '29', Rajasthan: '08', Odisha: '21',
};

/** [title, uom, dept code, base unit price, typical qty] */
const CATALOG: [string, string, string, number, number][] = [
  ['Desktop Computers (i5, 16GB, 512GB SSD)', 'Nos', 'SALES', 58_000, 400],
  ['Laptops for Field Engineers', 'Nos', 'CRET', 72_000, 150],
  ['Network Switches – 48 Port L3', 'Nos', 'GOV', 185_000, 40],
  ['Annual Maintenance of Data Centre UPS', 'Months', 'OPS', 240_000, 12],
  ['Cloud Backup Services', 'TB-Month', 'GOV', 2_800, 1_200],
  ['Manpower – Facility Management Services', 'Man-Months', 'HR', 28_000, 600],
  ['Security Guard Services', 'Man-Months', 'HSSE', 24_500, 900],
  ['Housekeeping Services', 'Man-Months', 'OPS', 21_000, 720],
  ['Office Furniture – Modular Workstations', 'Nos', 'CRET', 34_000, 250],
  ['Printing Paper A4 75 GSM', 'Reams', 'FIN', 290, 20_000],
  ['Hiring of Vehicles (SUV) – Monthly Basis', 'Vehicle-Months', 'LOG', 62_000, 120],
  ['LT Electrical Cables (3.5C x 240 sqmm)', 'Metres', 'OPS', 2_150, 12_000],
  ['Distribution Transformers 500 kVA', 'Nos', 'OPS', 920_000, 18],
  ['Civil Works – Boundary Wall Repair', 'Lump Sum', 'LOG', 4_800_000, 1],
  ['Diesel Generator Set 250 kVA', 'Nos', 'OPS', 2_350_000, 6],
  ['Personal Protective Equipment Kits', 'Kits', 'HSSE', 3_400, 5_000],
  ['Fire Extinguishers (ABC, 9 kg)', 'Nos', 'HSSE', 3_900, 1_400],
  ['First Aid & Medical Consumables', 'Kits', 'HSSE-AUD', 1_250, 3_000],
  ['Lubricants & Hydraulic Oil', 'Litres', 'LOG', 310, 40_000],
  ['Spare Parts – Centrifugal Pumps', 'Sets', 'OPS', 145_000, 30],
  ['LED Street Lights 90W', 'Nos', 'CRET', 6_800, 2_500],
  ['Annual Pest Control Services', 'Months', 'CRR', 38_000, 12],
  ['Payroll Software Subscription', 'User-Years', 'HR', 4_200, 1_500],
  ['Training Services – Safety Certification', 'Batches', 'HSSE-AUD', 85_000, 24],
  ['Audit & Assurance Consultancy', 'Man-Days', 'FIN', 18_000, 220],
];

const TENDER_TYPES: TenderType[] = ['bid', 'bid', 'bid_with_ra', 'reverse_auction', 'direct_purchase', 'l1_purchase', 'custom_bid'];
// Classification is derived from the index (not the random generator) so the rest of the sample data stays identical.
const TENDERING_METHODS: TenderingMethod[] = ['open', 'limited', 'open', 'single', 'limited'];
const TENDER_CATEGORIES: TenderCategory[] = ['omc', 'omc', 'imed'];
const FLOATED_BY: FloatedBy[] = ['cpo', 'hq'];
/** A few older-style tenders without classification, to show the "Details missing" state. */
const LEGACY_EVERY = 13;

const USERS: [string, string, AppUser['role'], string, string][] = [
  ['Ananya Rao', 'ananya.rao@example.org', 'admin', 'System Administrator', 'GOV'],
  ['Vikram Mehta', 'vikram.mehta@example.org', 'manager', 'Chief Manager – Contracts', 'LOG'],
  ['Priya Nair', 'priya.nair@example.org', 'editor', 'Contracts Officer', 'LOG'],
  ['Rahul Verma', 'rahul.verma@example.org', 'editor', 'Assistant Manager – Projects', 'OPS'],
  ['Sneha Kulkarni', 'sneha.kulkarni@example.org', 'viewer', 'Finance Executive', 'FIN'],
  ['Arjun Singh', 'arjun.singh@example.org', 'auditor', 'Internal Auditor', 'FIN'],
];

const DOC_TITLES: Record<DocumentType, string> = {
  contract: 'GeM Contract',
  purchase_order: 'Purchase Order',
  award_letter: 'Letter of Award',
  bid_document: 'Bid Document',
  invoice: 'Invoice',
  amendment: 'Amendment Order',
  performance_bank_guarantee: 'Performance Bank Guarantee',
  inspection_report: 'Inspection Report',
  delivery_challan: 'Delivery Challan',
  completion_certificate: 'Completion Certificate',
  other: 'Supporting Document',
};

let cached: SeedData | null = null;

export function buildSeed(today: string = todayISO()): SeedData {
  if (cached) return cached;
  const r = rng(20261002);
  const pick = <T,>(arr: readonly T[]) => arr[Math.floor(r() * arr.length)];
  const between = (min: number, max: number) => Math.floor(min + r() * (max - min + 1));
  const ts = (iso: string, h = between(9, 18), m = between(0, 59)) =>
    `${iso}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00.000Z`;

  const departments: Department[] = DEPARTMENTS.map(([code, name], i) => ({ id: uid(1, i), code, name, isActive: true }));
  const deptByCode = Object.fromEntries(departments.map((d) => [d.code, d]));
  const locations: Location[] = LOCATIONS.map(([code, name, state], i) => ({ id: uid(2, i), code, name, state, isActive: true }));

  const users: AppUser[] = USERS.map(([fullName, email, role, designation, dept], i) => ({
    id: uid(9, i),
    email,
    fullName,
    role,
    designation,
    departmentId: deptByCode[dept].id,
    departmentName: deptByCode[dept].name,
    // Every other sample user has a mobile number (deterministic — no random draws).
    phone: i % 2 === 0 ? `+919${String(820000000 + i * 1234567).slice(0, 9)}` : null,
    canDeleteUsers: role === 'admin',
    isActive: true,
    lastLoginAt: ts(addDays(today, -between(0, 6))),
    createdAt: ts(addDays(today, -between(300, 500))),
  }));

  const vendors: Vendor[] = VENDORS.map(([name, city, state, msme], i) => {
    const sc = STATE_CODES[state] ?? '07';
    const pan = `AAB${String.fromCharCode(65 + i)}C${String(1000 + i * 37).slice(-4)}${String.fromCharCode(70 + (i % 10))}`;
    return {
      id: uid(3, i),
      vendorCode: `VND-${String(1001 + i)}`,
      name,
      gstin: `${sc}${pan}1Z${(i % 9) + 1}`,
      pan,
      msmeRegistered: msme,
      contactPerson: pick(['R. Sharma', 'K. Iyer', 'S. Banerjee', 'M. Khan', 'P. Desai', 'A. Reddy', 'N. Gupta']),
      email: `contracts@${name.toLowerCase().replace(/[^a-z]+/g, '').slice(0, 14)}.example.com`,
      phone: `+91 ${between(70000, 99999)} ${between(10000, 99999)}`,
      address: `${between(1, 220)}, Industrial Area Phase ${between(1, 3)}`,
      city,
      state,
      isActive: true,
      createdAt: ts(addDays(today, -between(400, 900))),
      // Technical contact for two of every three vendors (deterministic — no random draws).
      ...(i % 3 !== 2
        ? {
            contactDesignation: 'Sales Manager',
            technicalContactName: ['V. Nair', 'S. Kulkarni', 'R. Menon', 'D. Singh', 'T. Joseph'][i % 5],
            technicalContactEmail: `support@${name.toLowerCase().replace(/[^a-z]+/g, '').slice(0, 14)}.example.com`,
            technicalContactPhone: `+918${String(450000000 + i * 7654321).slice(0, 9)}`,
            technicalContactDesignation: 'Service Engineer',
          }
        : {}),
    };
  });

  const tenders: Tender[] = [];
  const quantity: QuantityTransaction[] = [];
  const financials: FinancialTransaction[] = [];
  const documents: TenderDocument[] = [];
  const amendments: Amendment[] = [];
  const audit: AuditLog[] = [];
  let auditId = 1;
  const actor = () => pick(users.filter((u) => u.role !== 'viewer' && u.role !== 'auditor'));
  const log = (a: Omit<AuditLog, 'id'>) => audit.push({ id: auditId++, ...a });

  const TOTAL = 64;
  for (let i = 0; i < TOTAL; i++) {
    const [title, uom, deptCode, unitPrice, typicalQty] = CATALOG[i % CATALOG.length];
    const vendor = vendors[(i * 5 + Math.floor(i / 3)) % vendors.length];
    const location = pick(locations);

    // Lifecycle mix. Active contracts get end dates spread so some expire soon / are expired.
    const roll = r();
    let lifecycle: TenderLifecycleStatus =
      roll < 0.07 ? 'draft' : roll < 0.8 ? 'active' : roll < 0.9 ? 'completed' : roll < 0.95 ? 'terminated' : 'cancelled';

    const durationDays = pick([180, 270, 365, 365, 540, 730]);
    let endOffset: number; // relative to today
    if (lifecycle === 'active') {
      const b = r();
      endOffset = b < 0.12 ? -between(5, 90) : b < 0.35 ? between(3, 60) : between(61, Math.max(62, durationDays - 30));
    } else if (lifecycle === 'completed' || lifecycle === 'terminated') {
      endOffset = -between(20, 300);
    } else {
      endOffset = between(90, 500);
    }
    const endDate = addDays(today, endOffset);
    const startDate = addDays(endDate, -durationDays);
    const awardDate = addDays(startDate, -between(5, 25));
    const year = Number(awardDate.slice(0, 4));
    const qty = Math.max(1, Math.round(typicalQty * (0.5 + r())));
    const value = Math.round(qty * unitPrice * (0.85 + r() * 0.3));
    const gemNum = String(5116877000000 + i * 7919 + between(0, 999)).padStart(13, '0');
    const isDraft = lifecycle === 'draft';
    const id = uid(4, i);
    const owner = actor();

    const tender: Tender = {
      id,
      gemId: `GEMC-${gemNum}`,
      // CRFQ Number (empty on the occasional older-style tender)
      tenderNumber: i % LEGACY_EVERY === 5 ? null : String(1_000_400_000 + i * 1373),
      tenderingMethod: i % LEGACY_EVERY === 5 ? null : TENDERING_METHODS[i % TENDERING_METHODS.length],
      tenderCategory: i % LEGACY_EVERY === 5 ? null : TENDER_CATEGORIES[i % TENDER_CATEGORIES.length],
      floatedBy: i % LEGACY_EVERY === 5 ? null : FLOATED_BY[(i >> 1) % FLOATED_BY.length],
      title,
      description: `Procurement of ${title.toLowerCase()} for ${location.name} under ${deptByCode[deptCode].name}.`,
      tenderType: pick(TENDER_TYPES),
      departmentId: deptByCode[deptCode].id,
      locationId: location.id,
      vendorId: isDraft ? null : vendor.id,
      poNumber: isDraft ? null : `PO/${deptCode}/${year}/${String(300 + i).padStart(5, '0')}`,
      contractNumber: isDraft ? null : `CN-${year}-${String(10_000 + i * 17).padStart(6, '0')}`,
      awardDate: isDraft ? null : awardDate,
      startDate: isDraft ? null : startDate,
      endDate: isDraft ? null : endDate,
      contractValue: value,
      currency: 'INR',
      totalQuantity: qty,
      unitOfMeasure: uom,
      lifecycleStatus: lifecycle,
      ownerId: owner.id,
      closedAt: lifecycle === 'completed' || lifecycle === 'terminated' ? ts(addDays(endDate, between(1, 15))) : null,
      closureRemarks:
        lifecycle === 'terminated' ? 'Terminated due to repeated delivery delays (risk & cost clause invoked).' :
        lifecycle === 'completed' ? 'All deliverables received and accepted.' : null,
      createdAt: ts(isDraft ? addDays(today, -between(2, 40)) : addDays(awardDate, -between(1, 10))),
      updatedAt: ts(addDays(today, -between(0, 30))),
    };
    tenders.push(tender);
    log({
      occurredAt: tender.createdAt, actorId: owner.id, actorEmail: owner.email, actorName: owner.fullName,
      action: 'insert', entityType: 'tenders', entityId: id, tenderId: id, gemId: tender.gemId,
      changes: { gem_id: tender.gemId, title }, summary: `Created tender ${tender.gemId}`,
    });

    if (isDraft || lifecycle === 'cancelled') continue;

    // ---- quantity consumption ledger
    const elapsed = Math.min(1, Math.max(0, (durationDays - Math.max(0, endOffset)) / durationDays));
    const targetUtil =
      lifecycle === 'completed' ? 0.97 + r() * 0.03 :
      lifecycle === 'terminated' ? 0.3 + r() * 0.3 :
      Math.min(1, elapsed * (0.5 + r() * 0.55));
    const consumedTarget = Math.round(qty * targetUtil);
    const txCount = Math.max(1, Math.min(8, Math.round(consumedTarget > 0 ? between(2, 8) : 0)));
    let remaining = consumedTarget;
    const spanDays = Math.max(1, Math.min(durationDays, durationDays - Math.max(0, endOffset)));
    for (let k = 0; k < txCount && remaining > 0; k++) {
      const q = k === txCount - 1 ? remaining : Math.max(1, Math.round((consumedTarget / txCount) * (0.6 + r() * 0.8)));
      const take = Math.min(q, remaining);
      remaining -= take;
      const d = addDays(startDate, Math.round(((k + 1) / (txCount + 1)) * spanDays));
      const who = actor();
      const qtx: QuantityTransaction = {
        id: uid(5, i * 20 + k),
        tenderId: id,
        txnDate: d,
        txnType: 'consumption',
        quantity: take,
        referenceNo: `SRV/${deptCode}/${d.slice(0, 4)}/${between(1000, 9999)}`,
        locationId: location.id,
        remarks: k === 0 ? 'First delivery received and inspected' : null,
        createdBy: who.id,
        createdByName: who.fullName,
        createdAt: ts(d),
      };
      quantity.push(qtx);
      if (k >= txCount - 2) {
        log({
          occurredAt: qtx.createdAt, actorId: who.id, actorEmail: who.email, actorName: who.fullName,
          action: 'insert', entityType: 'quantity_transactions', entityId: qtx.id, tenderId: id, gemId: tender.gemId,
          changes: { quantity: take, txn_type: 'consumption' }, summary: `Recorded consumption of ${take} ${uom}`,
        });
      }
    }

    // ---- financials: invoices against consumption, payments for most
    const consumedValue = (consumedTarget / qty) * value;
    const invCount = Math.min(6, Math.max(1, Math.ceil(txCount / 2)));
    for (let k = 0; k < invCount; k++) {
      const amt = Math.round(consumedValue / invCount);
      if (amt <= 0) continue;
      const d = addDays(startDate, Math.round(((k + 1) / (invCount + 1)) * spanDays) + 7);
      if (d > today) continue;
      const invNo = `INV/${vendor.vendorCode}/${between(100, 999)}`;
      const paid = r() < 0.8 || lifecycle !== 'active';
      financials.push({
        id: uid(6, i * 40 + k * 2), tenderId: id, txnDate: d, txnType: 'invoice', status: paid ? 'approved' : 'pending',
        amount: amt, taxAmount: Math.round(amt * 0.18), referenceNo: invNo, dueDate: addDays(d, 30), remarks: null, createdAt: ts(d),
      });
      if (paid) {
        const pd = addDays(d, between(12, 40));
        if (pd <= today) {
          financials.push({
            id: uid(6, i * 40 + k * 2 + 1), tenderId: id, txnDate: pd, txnType: 'payment', status: 'paid',
            amount: amt, taxAmount: 0, referenceNo: `UTR${between(100000000, 999999999)}`, dueDate: null,
            remarks: `Against ${invNo}`, createdAt: ts(pd),
          });
        }
      }
    }
    if (lifecycle === 'terminated' || r() < 0.08) {
      financials.push({
        id: uid(6, i * 40 + 39), tenderId: id, txnDate: addDays(endDate, -10 > endOffset ? -5 : -30),
        txnType: 'penalty', status: 'approved', amount: Math.round(value * 0.02), taxAmount: 0,
        referenceNo: `LD/${deptCode}/${i}`, dueDate: null, remarks: 'Liquidated damages for delayed delivery', createdAt: ts(addDays(endDate, -5)),
      });
    }

    // ---- documents (some pending)
    const docPlan: [DocumentType, boolean][] = [
      ['contract', true],
      ['award_letter', true],
      ['purchase_order', true],
      ['performance_bank_guarantee', r() > 0.25],
      ['inspection_report', r() > 0.35],
    ];
    if (lifecycle === 'completed') docPlan.push(['completion_certificate', r() > 0.3]);
    docPlan.forEach(([docType, received], k) => {
      const dId = uid(7, i * 20 + k);
      const uploadedBy = actor();
      const upDate = addDays(awardDate, between(1, 20) + k * 3);
      const ext = docType === 'inspection_report' ? 'xlsx' : 'pdf';
      const fileName = `${tender.gemId}_${docType}.${ext}`;
      const isReceived = received && upDate <= today;
      documents.push({
        id: dId,
        tenderId: id,
        docType,
        title: DOC_TITLES[docType],
        status: isReceived ? (r() > 0.4 ? 'verified' : 'uploaded') : 'pending',
        storagePath: isReceived ? `${id}/${dId}/${fileName}` : null,
        fileName: isReceived ? fileName : null,
        mimeType: isReceived ? (ext === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') : null,
        sizeBytes: isReceived ? between(80_000, 4_500_000) : null,
        version: 1,
        dueDate: isReceived ? null : addDays(today, between(-20, 25)),
        uploadedByName: isReceived ? uploadedBy.fullName : null,
        uploadedAt: isReceived ? ts(upDate) : null,
        verifiedAt: null,
        remarks: null,
        createdAt: ts(awardDate),
      });
      if (isReceived && k < 2) {
        log({
          occurredAt: ts(upDate), actorId: uploadedBy.id, actorEmail: uploadedBy.email, actorName: uploadedBy.fullName,
          action: 'insert', entityType: 'documents', entityId: dId, tenderId: id, gemId: tender.gemId,
          changes: { doc_type: docType, file_name: fileName }, summary: `Uploaded ${DOC_TITLES[docType]}`,
        });
      }
    });

    // ---- amendments for ~30% of contracts
    if (r() < 0.3 && lifecycle !== 'terminated') {
      const aType = pick(['extension', 'quantity_change', 'value_change'] as const);
      const aDate = addDays(startDate, Math.round(durationDays * 0.6));
      if (aDate <= today) {
        const am: Amendment = {
          id: uid(8, i),
          tenderId: id,
          amendmentNo: 1,
          amendmentDate: aDate,
          amendmentType: aType,
          status: 'approved',
          reason:
            aType === 'extension' ? 'Extension of contract period to cover pending deliveries' :
            aType === 'quantity_change' ? 'Additional requirement from user department (within 25% option clause)' :
            'Price variation as per contract clause',
          // Seeded tenders already reflect the amended values (the amendment was applied).
          previousEndDate: aType === 'extension' ? addDays(endDate, -90) : null,
          newEndDate: aType === 'extension' ? endDate : null,
          previousQuantity: aType === 'quantity_change' ? Math.round(qty / 1.2) : null,
          newQuantity: aType === 'quantity_change' ? qty : null,
          previousValue: aType !== 'extension' ? Math.round(value / 1.15) : null,
          newValue: aType !== 'extension' ? value : null,
          approvedAt: ts(addDays(aDate, 2)),
          createdAt: ts(aDate),
        };
        amendments.push(am);
        const who = actor();
        log({
          occurredAt: am.createdAt, actorId: who.id, actorEmail: who.email, actorName: who.fullName,
          action: 'insert', entityType: 'amendments', entityId: am.id, tenderId: id, gemId: tender.gemId,
          changes: { amendment_type: aType }, summary: `Amendment #1 (${aType.replace('_', ' ')}) approved`,
        });
      }
    }

    if (lifecycle === 'completed' || lifecycle === 'terminated') {
      const who = actor();
      log({
        occurredAt: tender.closedAt!, actorId: who.id, actorEmail: who.email, actorName: who.fullName,
        action: 'update', entityType: 'tenders', entityId: id, tenderId: id, gemId: tender.gemId,
        changes: { lifecycle_status: { from: 'active', to: lifecycle } }, summary: `Status changed to ${lifecycle}`,
      });
    }
  }

  // ---- bills are entered once paid: turn each "invoice + separate payment" pair of the generated data into one
  //      Paid invoice with its GR number and bill period (the calendar month before the invoice date).
  let grSeq = 0;
  const prevMonth = (iso: string): [string, string] => {
    const [y, m] = iso.split('-').map(Number);
    const py = m === 1 ? y - 1 : y;
    const pm = m === 1 ? 12 : m - 1;
    const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
    const mm = String(pm).padStart(2, '0');
    return [`${py}-${mm}-01`, `${py}-${mm}-${String(last).padStart(2, '0')}`];
  };
  const paymentsAgainst = new Map(financials.filter((f) => f.txnType === 'payment' && f.remarks?.startsWith('Against ')).map((f) => [`${f.tenderId}|${f.remarks!.slice(8)}`, f]));
  const dropIds = new Set<string>();
  financials.forEach((f) => {
    if (f.txnType !== 'invoice') return;
    const pay = paymentsAgainst.get(`${f.tenderId}|${f.referenceNo}`);
    if (!pay) return;
    const [from, to] = prevMonth(f.txnDate);
    Object.assign(f, { status: 'paid', grPoType: 'GR', grPoNumber: String(5_000_100 + grSeq++), billPeriodFrom: from, billPeriodTo: to, txnDate: pay.txnDate });
    dropIds.add(pay.id);
  });
  for (let k = financials.length - 1; k >= 0; k--) if (dropIds.has(financials[k].id)) financials.splice(k, 1);

  // ---- order type and additional-budget tag (deterministic — no random draws, so the rest stays identical)
  tenders.forEach((t) => {
    t.orderType = 'original';
    t.repeatOfTenderId = null;
    t.budgetStatus = 'none';
  });
  tenders.forEach((t, i) => {
    if (i % 9 !== 4 || t.lifecycleStatus === 'draft') return;
    // Repeat of an earlier Original tender with the same vendor (or the closest earlier one).
    const earlier = tenders.slice(0, i).filter((x) => x.orderType === 'original' && x.lifecycleStatus !== 'draft' && x.awardDate && t.awardDate && x.awardDate <= t.awardDate);
    const original = earlier.find((x) => x.vendorId === t.vendorId) ?? earlier[earlier.length - 1];
    if (original) { t.orderType = 'repeat'; t.repeatOfTenderId = original.id; }
  });
  const sought = (t: Tender) => Math.round((t.contractValue * 0.15) / 1000) * 1000;
  tenders.forEach((t, i) => {
    if (t.lifecycleStatus !== 'active') return;
    const requestedOn = addDays(today, -((i % 5) * 9 + 6));
    if (i % 11 === 3) {
      Object.assign(t, { budgetStatus: 'needed', budgetAmountSought: sought(t), budgetReason: 'Consumption is running ahead of the contract period.' });
    } else if (i % 13 === 7) {
      Object.assign(t, { budgetStatus: 'requested', budgetAmountSought: sought(t), budgetReason: 'Additional quantity required for new sites.', budgetRequestedOn: requestedOn });
    } else if (i % 17 === 9) {
      Object.assign(t, {
        budgetStatus: 'approved', budgetAmountSought: sought(t), budgetReason: 'Extended scope approved by the department.', budgetRequestedOn: requestedOn,
        budgetDecisionDate: addDays(requestedOn, 4), budgetDecisionRemarks: 'Approved within delegated powers.',
      });
    } else if (i % 19 === 12) {
      Object.assign(t, {
        budgetStatus: 'rejected', budgetAmountSought: sought(t), budgetReason: 'Higher rates quoted by the vendor.', budgetRequestedOn: requestedOn,
        budgetDecisionDate: addDays(requestedOn, 5), budgetDecisionRemarks: 'Re-tender instead of extending the contract.',
      });
    }
  });

  // ---- notifications for the first user (demo signed-in user)
  const me = users[0];
  const notifications: AppNotification[] = [];
  tenders
    .filter((t) => t.lifecycleStatus === 'active' && t.endDate && t.endDate >= today && t.endDate <= addDays(today, 30))
    .slice(0, 5)
    .forEach((t, k) => {
      notifications.push({
        id: uid(10, k), userId: me.id, tenderId: t.id, kind: 'expiry', severity: 'warning',
        title: `Contract ${t.gemId} expires soon`, body: `${t.title} ends on ${t.endDate}.`,
        link: `/tenders/${t.id}`, readAt: k > 2 ? ts(addDays(today, -1)) : null, createdAt: ts(addDays(today, -k)),
      });
    });
  documents
    .filter((d) => d.status === 'pending' && d.dueDate && d.dueDate < today)
    .slice(0, 3)
    .forEach((d, k) => {
      const t = tenders.find((x) => x.id === d.tenderId)!;
      notifications.push({
        id: uid(11, k), userId: me.id, tenderId: t.id, kind: 'document_pending', severity: 'critical',
        title: `Overdue: ${d.title}`, body: `${t.gemId} — document was due ${d.dueDate}.`,
        link: `/tenders/${t.id}?tab=documents`, readAt: null, createdAt: ts(addDays(today, -k - 1)),
      });
    });

  audit.sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
  notifications.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

  cached = { users, departments, locations, vendors, tenders, quantity, financials, documents, amendments, audit, notifications };
  return cached;
}
